/* Event-code gate: the student types the code their teacher gave them; it's
   validated automatically as they type (debounced) and Continue unlocks when
   it checks out — no extra button. A valid code loads that event's groups,
   genders and careers. Deep links with ?access_code arrive pre-answered. */
import { useEffect, useState } from 'react';
import { EVENT_ERROR, applyCategoriesConfig, applyGenderConfig, applyGroupsConfig } from '../data';
import { useSurvey } from '../SurveyContext';

export function EventCodeStep() {
  const surveyFlow = useSurvey();
  const [code, setCode] = useState(surveyFlow.answers.accessCode);
  const [busy, setBusy] = useState(false);
  // A deep-linked ?access_code that failed the event gate surfaces its message here.
  const [error, setError] = useState(EVENT_ERROR);
  const loaded = surveyFlow.answers._eventLoaded === true;
  const matchesLoaded =
    loaded && code.trim().toUpperCase() === surveyFlow.answers.accessCode.toUpperCase();

  const check = async (c: string) => {
    if (!c || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/realu-next/event-config?access_code=' + encodeURIComponent(c));
      const cfg = await res.json();
      if (cfg && cfg.ok) {
        applyGroupsConfig(cfg.groups);
        applyGenderConfig(cfg.genders);
        applyCategoriesConfig(cfg.categories);
        // switching events invalidates any group/session/career already picked;
        // a picked gender survives only if the new event still offers it
        const genderStillOffered =
          Array.isArray(cfg.genders) &&
          cfg.genders.some((g: { value: string }) => g.value === surveyFlow.answers.gender);
        surveyFlow.setA({
          accessCode: cfg.event.accessCode,
          eventId: cfg.event.id,
          _eventLoaded: true,
          gender: genderStillOffered ? surveyFlow.answers.gender : '',
          teacherId: '',
          sessionId: '',
          careerCategories: [],
          career: null,
        });
      } else {
        surveyFlow.setA({ _eventLoaded: false, eventId: '' });
        setError((cfg && cfg.message) || 'Something went wrong — please try again.');
      }
    } catch (e) {
      setError('Something went wrong — check your connection and try again.');
    }
    setBusy(false);
  };

  // Auto-validate shortly after the student stops typing.
  useEffect(() => {
    const c = code.trim();
    if (!c || matchesLoaded) return;
    const t = setTimeout(() => check(c), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="intake-eventcode">
      <input
        className="intake-text intake-eventcode-input"
        type="text"
        value={code}
        placeholder="Event code"
        autoComplete="off"
        onChange={(e) => {
          setCode(e.target.value);
          // editing away from a validated code re-locks Continue
          if (
            loaded &&
            e.target.value.trim().toUpperCase() !== surveyFlow.answers.accessCode.toUpperCase()
          ) {
            surveyFlow.setA({ _eventLoaded: false });
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (matchesLoaded) surveyFlow.doNext();
            else check(code.trim());
          }
        }}
      />
      {busy ? <p className="intake-eventcode-checking">Checking…</p> : null}
      {!busy && matchesLoaded && !error ? (
        <p className="intake-eventcode-ok">Event found — hit Continue!</p>
      ) : null}
      {!busy && error ? <p className="intake-eventcode-err">{error}</p> : null}
    </div>
  );
}
